import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

/**
 * Largest header logo slot across the website designs. In website-templates
 * every design shows the header logo 44–48 px tall and 180–200 px wide at
 * most, so a logo that is sharp here is sharp in all of them.
 */
export const WEBSITE_LOGO_SLOT = { height: 48, maxWidth: 200 } as const;

/**
 * Narrowest width-to-height ratio the designs show without the agency name.
 * Narrower logos count as square marks (BrandLogo's `WIDE_LOGO_RATIO`).
 */
export const WEBSITE_LOGO_WIDE_RATIO = 1.8;

/** Pixel density the size check assumes, so logos stay sharp on 2x screens. */
const SHARP_PIXEL_DENSITY = 2;

/** Translation keys of the logo recommendations, in display order. */
export const WEBSITE_LOGO_SPEC_KEYS: readonly TranslationKey[] = [
  'websites.settings.logo.specs.shape',
  'websites.settings.logo.specs.background',
  'websites.settings.logo.specs.colors',
  'websites.settings.logo.specs.square',
];

/** Natural size of an image, in pixels. */
export interface WebsiteLogoSize {
  /** Natural width. */
  width: number;
  /** Natural height. */
  height: number;
}

/** One finding about how an uploaded logo will look in the designs. */
export type WebsiteLogoFinding =
  | { kind: 'ideal' }
  | { kind: 'square' }
  | { kind: 'small'; minWidth: number; minHeight: number };

/**
 * Checks a logo's natural size against the designs' header slot: whether it
 * is large enough to stay sharp and wide enough to show without the agency
 * name. SVG logos skip the size check because they scale without blurring.
 *
 * @param size - Natural width and height of the logo.
 * @param isVector - Whether the logo is an SVG.
 * @returns The findings, most important first; empty when the size is unknown.
 */
export const checkWebsiteLogo = (
  { width, height }: WebsiteLogoSize,
  isVector: boolean
): WebsiteLogoFinding[] => {
  if (width <= 0 || height <= 0) return [];

  const ratio = width / height;
  const shownHeight = Math.min(
    WEBSITE_LOGO_SLOT.height,
    WEBSITE_LOGO_SLOT.maxWidth / ratio
  );
  const minHeight = Math.ceil(shownHeight * SHARP_PIXEL_DENSITY);
  const findings: WebsiteLogoFinding[] = [];

  if (!isVector && height < minHeight) {
    findings.push({
      kind: 'small',
      minWidth: Math.ceil(minHeight * ratio),
      minHeight,
    });
  }
  if (ratio < WEBSITE_LOGO_WIDE_RATIO) {
    findings.push({ kind: 'square' });
  }

  return findings.length > 0 ? findings : [{ kind: 'ideal' }];
};

/**
 * Tells whether a logo URL points at an SVG file, ignoring any query string.
 *
 * @param url - Absolute or relative logo URL.
 * @returns Whether the file is an SVG.
 */
export const isVectorLogoUrl = (url: string): boolean => {
  try {
    return new URL(url, 'https://logo.invalid').pathname
      .toLowerCase()
      .endsWith('.svg');
  } catch {
    return false;
  }
};
